import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Sparkles, Loader } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

const BACKEND_URL = config.BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddToolPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const { isAdmin, getAuthHeader, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    url: '',
    name: '',
    description: '',
    icon_url: '',
    category: '',
    tags: ''
  });

  useEffect(() => {
    if (!authLoading && !isAdmin()) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
    }
  }, [isAdmin, authLoading, navigate]);

  if (authLoading) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center">
      <div className="text-white">Loading...</div>
    </div>;
  }

  if (!isAdmin()) {
    return null;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleExtractInfo = async () => {
    if (!formData.url) {
      toast.error('Please enter a URL first');
      return;
    }

    setExtracting(true);
    try {
      const response = await axios.post(`${API}/tools/extract-url`, 
        { url: formData.url },
        { headers: getAuthHeader() }
      );
      
      setFormData({
        ...formData,
        name: response.data.name || '',
        description: response.data.description || '',
        icon_url: response.data.icon_url || '',
        category: response.data.category || '',
        tags: response.data.tags?.join(', ') || ''
      });
      
      toast.success('Information extracted successfully!');
    } catch (error) {
      console.error('Error extracting info:', error);
      toast.error('Failed to extract information');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.url) {
      toast.error('URL is required');
      return;
    }

    setLoading(true);
    try {
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const payload = {
        url: formData.url,
        name: formData.name || null,
        description: formData.description || null,
        icon_url: formData.icon_url || null,
        category: formData.category || null,
        tags: tagsArray.length > 0 ? tagsArray : null
      };

      await axios.post(`${API}/tools`, payload, {
        headers: getAuthHeader()
      });
      
      toast.success('Tool added successfully!');
      navigate('/browse');
    } catch (error) {
      console.error('Error adding tool:', error);
      toast.error(error.response?.data?.detail || 'Failed to add tool');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-6 py-12">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="gradient-text">Curate New Tool</span>
          </h1>
          <p className="text-lg text-gray-400">Add tools manually or leverage AI-powered metadata extraction for automated onboarding</p>
        </div>

        {/* Form */}
        <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* URL */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tool URL *</label>
              <div className="flex gap-2">
                <Input
                  data-testid="url-input"
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  required
                  className="flex-1 bg-gray-900/50 border-gray-600 text-white placeholder-gray-500"
                />
                <button
                  data-testid="extract-info-btn"
                  type="button"
                  onClick={handleExtractInfo}
                  disabled={extracting}
                  className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-400 font-medium hover:bg-purple-500/20 transition-colors disabled:opacity-50"
                >
                  {extracting ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  AI Extract
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Use AI Extract to automatically fill in the details</p>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tool Name</label>
              <Input
                data-testid="name-input"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Kubernetes"
                className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <Textarea
                data-testid="description-input"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of the tool"
                rows={3}
                className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500"
              />
            </div>

            {/* Icon URL */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Icon URL</label>
              <Input
                data-testid="icon-url-input"
                type="url"
                name="icon_url"
                value={formData.icon_url}
                onChange={handleChange}
                placeholder="https://example.com/icon.png"
                className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
              <Input
                data-testid="category-input"
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., Container Orchestration"
                className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
              <Input
                data-testid="tags-input"
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="kubernetes, docker, containers (comma-separated)"
                className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 pt-4">
              <button
                data-testid="submit-btn"
                type="submit"
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add Tool
                  </>
                )}
              </button>
              
              <button
                data-testid="cancel-btn"
                type="button"
                onClick={() => navigate('/browse')}
                className="px-8 py-4 rounded-xl bg-gray-700/50 text-white font-semibold hover:bg-gray-600/50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddToolPage;